import React, { useState, useEffect } from "react";
import { collection, getDocs, updateDoc, doc } from "firebase/firestore";
import { db } from "../firebaseConfig";
import bookImage from "../../assets/staticebook.png";
const ApprovePayment = () => {
  const [activeTab, setActiveTab] = useState("pending");
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  // Fetch payments from Firebase
  const fetchPayments = async () => {
    setLoading(true);
    try {
      const paymentsCollection = collection(db, "payments");
      const paymentsSnapshot = await getDocs(paymentsCollection);
      const paymentsList = paymentsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setPayments(paymentsList);
    } catch (error) {
      console.error("Error fetching payments:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const handleApprove = async (paymentId) => {
    try {
      // Update payment status in Firebase
      const paymentRef = doc(db, "payments", paymentId);
      await updateDoc(paymentRef, { status: "confirmed" });

      // Update local state
      setPayments((prev) =>
        prev.map((payment) =>
          payment.id === paymentId
            ? { ...payment, status: "confirmed" }
            : payment
        ),
        alert("Payment approved successfully!")
      );
    } catch (error) {
      console.error("Error approving payment:", error);
    }
  };

  // Safely format values for display
  const safeValue = (value) => value || "N/A";
  const safeLowercase = (value) => (value || "").toString().toLowerCase();

  // Filter payments with safe checks
  const filteredPayments = payments
    .filter((payment) => payment.status === activeTab)
    .filter((payment) => {
      if (!searchTerm) return true;

      const term = searchTerm.toLowerCase();
      return (
        safeLowercase(payment.emailID).includes(term) ||
        safeLowercase(payment.transactionId).includes(term) ||
        safeLowercase(payment.bookTitle).includes(term)
      );
    });

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";

    try {
      const options = {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      };
      return new Date(dateString).toLocaleDateString(undefined, options);
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Invalid Date";
    }
  };

  // Count payments by status
  const pendingCount = payments.filter((p) => p.status === "pending").length;
  const confirmedCount = payments.filter(
    (p) => p.status === "confirmed"
  ).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-10 text-center">
          <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent">
            Payment Approval Dashboard
          </h1>
          <p className="text-gray-400 mt-2 max-w-2xl mx-auto">
            Manage and approve customer payments for eBook purchases
          </p>
        </div>

        <div className="bg-gray-800/50 backdrop-blur-md rounded-2xl border border-gray-700 shadow-xl overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-gray-700">
            <button
              className={`py-4 px-6 font-medium text-sm md:text-base flex-1 text-center transition-all ${
                activeTab === "pending"
                  ? "text-cyan-400 border-b-2 border-cyan-400 bg-gray-900/30"
                  : "text-gray-400 hover:text-gray-300"
              }`}
              onClick={() => setActiveTab("pending")}
            >
              <div className="flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Pending Payments
                {pendingCount > 0 && (
                  <span className="ml-2 bg-orange-500 text-white text-xs rounded-full px-2 py-0.5">
                    {pendingCount}
                  </span>
                )}
              </div>
            </button>

            <button
              className={`py-4 px-6 font-medium text-sm md:text-base flex-1 text-center transition-all ${
                activeTab === "confirmed"
                  ? "text-green-400 border-b-2 border-green-400 bg-gray-900/30"
                  : "text-gray-400 hover:text-gray-300"
              }`}
              onClick={() => setActiveTab("confirmed")}
            >
              <div className="flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                Confirmed Payments
                {confirmedCount > 0 && (
                  <span className="ml-2 bg-green-500 text-white text-xs rounded-full px-2 py-0.5">
                    {confirmedCount}
                  </span>
                )}
              </div>
            </button>
          </div>

          {/* Search bar */}
          <div className="p-4 border-b border-gray-700">
            <div className="relative max-w-md mx-auto">
              <input
                type="text"
                placeholder="Search payments by email, transaction ID, or book title..."
                className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-500 text-sm"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5 absolute right-3 top-2.5 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>

          {/* Payment List */}
          <div className="overflow-x-auto">
            {loading ? (
              <div className="min-h-[300px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500"></div>
                <span className="ml-4">Loading payments...</span>
              </div>
            ) : filteredPayments.length === 0 ? (
              <div className="min-h-[300px] flex flex-col items-center justify-center p-8 text-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 text-gray-600 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <h3 className="text-xl font-medium text-gray-400 mb-2">
                  No {activeTab} payments found
                </h3>
                <p className="text-gray-600 max-w-md">
                  {activeTab === "pending"
                    ? "All payments have been approved! No pending payments at this time."
                    : "No confirmed payments yet. Approve payments to see them here."}
                </p>
                <button
                  onClick={fetchPayments}
                  className="mt-4 px-4 py-2 bg-gray-700 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Refresh Data
                </button>
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-700">
                <thead className="bg-gray-900/50">
                  <tr>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Book
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Transaction
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Customer
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Amount
                    </th>
                    <th
                      scope="col"
                      className="py-3 px-4 text-right text-xs font-medium text-gray-400 uppercase tracking-wider"
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {filteredPayments.map((payment) => (
                    <tr
                      key={payment.id}
                      className="hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="py-4 px-4">
                        <div className="flex items-center">
                          <img src={bookImage} alt="book-image" className="bg-gray-700 border-2 border-dashed rounded-xl w-16 h-16 mr-3" />
                          <div className="font-medium max-w-[180px] truncate">
                            {safeValue(payment.bookTitle)}
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-4 text-sm text-cyan-300 font-mono max-w-[120px] truncate">
                        {safeValue(payment.transactionId)}
                      </td>
                      <td className="py-4 px-4 text-sm max-w-[180px] truncate">
                        {safeValue(payment.emailID)}
                      </td>
                      <td className="py-4 px-4 text-sm text-gray-400">
                        {formatDate(payment.timestamp?.toDate?.())}
                      </td>
                      <td className="py-4 px-4 text-lg font-semibold text-green-400">
                        {safeValue(payment.amount)}
                      </td>
                      <td className="py-4 px-4 text-right">
                        {payment.status === "pending" ? (
                          <button
                            onClick={() => handleApprove(payment.id)}
                            className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-green-600 to-cyan-600 rounded-lg font-medium hover:from-green-500 hover:to-cyan-500 transition-all"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Approve
                          </button>
                        ) : (
                          <div className="inline-flex items-center px-4 py-2 bg-green-500/20 text-green-400 rounded-lg font-medium">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-1"
                              viewBox="0 0 20 20"
                              fill="currentColor"
                            >
                              <path
                                fillRule="evenodd"
                                d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            Confirmed
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Stats footer */}
          <div className="bg-gray-900/50 p-4 flex flex-wrap items-center justify-between">
            <div className="text-sm text-gray-400">
              Showing {filteredPayments.length} of{" "}
              {activeTab === "pending" ? pendingCount : confirmedCount}{" "}
              {activeTab} payments
            </div>
            <div className="flex space-x-4">
              <div className="flex items-center">
                <div className="w-3 h-3 bg-orange-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-400">
                  Pending: {pendingCount}
                </span>
              </div>
              <div className="flex items-center">
                <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
                <span className="text-sm text-gray-400">
                  Confirmed: {confirmedCount}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovePayment;
